import { FC } from "dreamland/core";
import { getWebpPath, getWebpSrcset } from "../lib/images";

/**
 * Renders an `<img>` wrapped in a `<picture>` with a WebP `<source>` whenever
 * the `src` is a local `.jpg` / `.jpeg` path that has a sibling `.webp` file
 * generated by our build. Browsers without WebP support fall back to the
 * original JPEG via the `<img>` element.
 *
 * Used as the MDX `img` component override in BlogPost so every blog
 * `![alt](path.jpg)` markdown image and `<img>` JSX tag automatically gets a
 * WebP source.
 *
 * Pass-through behaviour: any extra props on Pic are forwarded to the inner
 * `<img>` (alt, title, width, height, loading, class, etc.). Non-JPEG paths,
 * external URLs, and missing `src` values render a plain `<img>` unchanged.
 */
function Pic(this: FC<Record<string, unknown>>) {
	const src = this.src as string | undefined;
	const srcset = (this.srcset as string | undefined) ?? undefined;

	// Build a clean passthrough for the inner <img>, excluding dreamland
	// internals (`cx`, `root`) and the MDX `components` map (which can leak
	// in through `this` when Pic is used as an MDX component override).
	const imgProps: Record<string, unknown> = {};
	for (const [key, value] of Object.entries(this)) {
		if (key === "cx" || key === "root" || key === "components") continue;
		imgProps[key] = value;
	}

	const webpSrc = getWebpPath(src);
	if (!webpSrc) {
		return <img {...imgProps} />;
	}

	const webpSrcset = srcset ? getWebpSrcset(srcset) : webpSrc;

	return (
		<picture>
			<source type="image/webp" srcset={webpSrcset} />
			<img {...imgProps} />
		</picture>
	);
}

export default Pic;
